import React, { useState } from 'react';
import { Routes, Route, Link, useSearchParams } from 'react-router-dom';
import TodoAppAdvanced from './TodoAppAdvanced';
import '../../styles/TodoApp.css';

interface Todo {
  id: number;
  text: string;
  completed: boolean;
}

const TodoAppMain: React.FC = () => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [nextId, setNextId] = useState(1);

  const addTodo = () => {
    if (inputValue.trim()) {
      setTodos([...todos, { id: nextId, text: inputValue.trim(), completed: false }]);
      setInputValue('');
      setNextId(nextId + 1);
    }
  };

  const toggleTodo = (id: number) => {
    setTodos(todos.map(todo =>
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  const deleteTodo = (id: number) => {
    setTodos(todos.filter(todo => todo.id !== id));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addTodo();
    }
  };

  return (
    <div className="todo-app-10">
      <div className="header">
        <Link to="/" className="back-btn">Back</Link>
        <h1>Todo List</h1>
        <span className="version">v1.0</span>
      </div>
      
      <div className="add-todo">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Add a new todo..."
        />
        <button onClick={addTodo}>Add</button>
      </div>

      <div className="todo-list">
        {todos.length === 0 ? (
          <p className="no-todos">No todos yet. Add one above!</p>
        ) : (
          todos.map(todo => (
            <div key={todo.id} className={`todo-item ${todo.completed ? 'completed' : ''}`}>
              <input
                type="checkbox"
                checked={todo.completed}
                onChange={() => toggleTodo(todo.id)}
              />
              <span className="todo-text">{todo.text}</span>
              <button onClick={() => deleteTodo(todo.id)} className="delete-btn">
                Delete
              </button>
            </div>
          ))
        )}
      </div>

      <div className="footer">
        <Link to="/todo-app/about">About</Link>
      </div>
    </div>
  );
};

const TodoAbout: React.FC = () => {
  return (
    <div className="todo-about-10">
      <Link to="/todo-app" className="back-btn">Back to Todo</Link>
      <h1>About Todo App</h1>
      
      <div className="prompt">
        <h2>Prompt</h2>
        <p>"Build a basic todo list app with add, delete, and mark complete functionality"</p>
      </div>

      <div className="features">
        <h2>Synoppy 1.0 Features</h2>
        <ul>
          <li>Basic add/delete/complete functionality</li>
          <li>Simple HTML form inputs</li>
          <li>Minimal CSS styling</li>
          <li>No animations or sound effects</li>
          <li>Basic responsive design</li>
        </ul>
      </div>

      <Link to="/" className="home-link">Home</Link>
    </div>
  );
};

const VersionSwitcher: React.FC<{ currentVersion: string }> = ({ currentVersion }) => {
  return (
    <div className="version-switcher-todo">
      <h3>Compare Versions:</h3>
      <div className="version-buttons-todo">
        <Link 
          to="?version=1.0" 
          className={`version-btn-todo ${currentVersion === '1.0' ? 'active' : ''} v10`}
        >
          Synoppy 1.0 - Basic
        </Link>
        <Link 
          to="?version=1.5" 
          className={`version-btn-todo ${currentVersion === '1.5' ? 'active' : ''} v15`}
        >
          Synoppy 1.5 - Enterprise
        </Link>
      </div>
    </div>
  );
};

const TodoApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const version = searchParams.get('version') || '1.5';
  
  if (version === '1.0') {
    return (
      <div className="todo-container">
        <Routes>
          <Route path="/" element={
            <div>
              <VersionSwitcher currentVersion="1.0" />
              <TodoAppMain />
            </div>
          } />
          <Route path="/about" element={<TodoAbout />} />
        </Routes>
      </div>
    );
  }
  
  return (
    <div className="todo-container">
      <Routes>
        <Route path="/" element={
          <div>
            <VersionSwitcher currentVersion="1.5" />
            <TodoAppAdvanced />
          </div>
        } />
        <Route path="/about" element={<TodoAbout />} />
      </Routes>
    </div>
  );
};

export default TodoApp;